import { useFetcher } from '@remix-run/react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '~/components/ui/Button';
import {
  IN_APP_SUPPORT_CATEGORY,
  IN_APP_SUPPORT_CATEGORY_VALUES,
  MIN_SUPPORT_MESSAGE_HINT,
  MIN_SUPPORT_MESSAGE_LENGTH,
} from '~/constants/in-app-support';
import { inAppSupportCategoryLabel } from '~/types/in-app-support';
import type { InAppSupportCategoryValue } from '~/types/in-app-support';

type SupportContactModalProps = {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  userName: string;
};

type ActionData = {
  success?: boolean;
  error?: string;
};

export function SupportContactModal({
  isOpen,
  onClose,
  userEmail,
  userName,
}: SupportContactModalProps) {
  const fetcher = useFetcher<ActionData>();
  const categoryId = useId();
  const messageId = useId();
  const [category, setCategory] = useState<InAppSupportCategoryValue>(
    IN_APP_SUPPORT_CATEGORY.GENERAL
  );
  const [message, setMessage] = useState('');

  const isSubmitting = fetcher.state === 'submitting';
  const isSuccess = fetcher.data?.success === true;
  const error = fetcher.data?.error;
  const isMessageTooShort = message.trim().length < MIN_SUPPORT_MESSAGE_LENGTH;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSubmitting, onClose]);

  useEffect(() => {
    if (isSuccess) {
      const timer = window.setTimeout(() => {
        setMessage('');
        setCategory(IN_APP_SUPPORT_CATEGORY.GENERAL);
        onClose();
      }, 2500);
      return () => window.clearTimeout(timer);
    }
  }, [isSuccess, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData();
    formData.set('category', category);
    formData.set('message', message);
    fetcher.submit(formData, { method: 'POST', action: '/api/support/contact' });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="support-modal-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close support dialog"
        onClick={() => !isSubmitting && onClose()}
      />
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 id="support-modal-title" className="text-lg font-semibold text-gray-900">
            Contact support
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {isSuccess ? (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
            <p className="text-sm font-medium text-green-900">Message sent</p>
            <p className="mt-1 text-sm text-green-800">
              We received your request and sent a copy to {userEmail}. Our team will follow up soon.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
              <p>
                <span className="font-medium text-gray-900">From:</span> {userName}
              </p>
              <p className="mt-1">
                <span className="font-medium text-gray-900">Email:</span> {userEmail}
              </p>
            </div>

            <div>
              <label htmlFor={categoryId} className="mb-1 block text-sm font-medium text-gray-700">
                Category
              </label>
              <select
                id={categoryId}
                name="category"
                value={category}
                onChange={e => setCategory(e.target.value as InAppSupportCategoryValue)}
                disabled={isSubmitting}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              >
                {IN_APP_SUPPORT_CATEGORY_VALUES.map(value => (
                  <option key={value} value={value}>
                    {inAppSupportCategoryLabel(value)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor={messageId} className="mb-1 block text-sm font-medium text-gray-700">
                Your message
              </label>
              <textarea
                id={messageId}
                name="message"
                value={message}
                onChange={e => setMessage(e.target.value)}
                disabled={isSubmitting}
                required
                minLength={MIN_SUPPORT_MESSAGE_LENGTH}
                maxLength={5000}
                rows={5}
                placeholder="Describe your question or issue..."
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
              {isMessageTooShort && (
                <p className="mt-1 text-xs text-gray-500">{MIN_SUPPORT_MESSAGE_HINT}</p>
              )}
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || isMessageTooShort}>
                {isSubmitting ? 'Sending...' : 'Send message'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
